/**
 * 運営者ログイン。ops 面は TOTP 必須なので 2 段階（パスワード → 6 桁コード）。
 *
 * リカバリコードも同じ画面で受け付ける。TOTP 端末を失った運営者が
 * 「別画面を探す」時間はインシデント対応中には無い。
 */
import * as React from 'react';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { KeyRound, Lock, Mail, ShieldCheck } from 'lucide-react';
import { Alert, AlertDescription, Button, Input, Label, ThemeToggle } from '@mail-sv/ui';
import { ApiError } from '../lib/api.js';
import { fetchPublicConfig, isMfaRequired, login, verifyMfa } from '../lib/auth.js';
import { qk } from '../lib/query-keys.js';
import { DevLoginButton } from '../components/dev-login-button.js';
import { errorMessageOf } from '../components/states.js';
import { navTo } from '../components/nav-link.js';
import { useSubmitOnce } from '../lib/submit-once.js';
import { RID } from './ids.js';

const routeApi = getRouteApi(RID.login);
const BRAND_NAME = import.meta.env.VITE_BRAND_NAME ?? 'Aurora Host';

type Stage = { kind: 'credentials' } | { kind: 'mfa'; ticket: string; methods: Array<'totp' | 'recovery'> };

export function LoginPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = useNavigate();
  const [stage, setStage] = React.useState<Stage>({ kind: 'credentials' });

  const config = useQuery({
    queryKey: qk.publicConfig(),
    queryFn: fetchPublicConfig,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const goHome = React.useCallback(() => {
    void navigate({ to: navTo(search.redirect ?? '/'), replace: true });
  }, [navigate, search.redirect]);

  const brandName = config.data?.serviceName ?? BRAND_NAME;

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-10">
      {/* 背景のオーロラ。ログイン画面だけの装飾で、本文の可読性には影響させない。 */}
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-40">
        <div className="brand-gradient absolute -left-32 -top-32 size-[28rem] rounded-full blur-3xl animate-aurora-slow" />
        <div className="brand-gradient absolute -bottom-40 -right-24 size-[32rem] rounded-full blur-3xl animate-aurora-slower" />
      </div>

      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>

      <div className="relative w-full max-w-sm space-y-6">
        <div className="space-y-2 text-center">
          <div className="brand-gradient mx-auto flex size-12 items-center justify-center rounded-xl text-white shadow-lg shadow-primary/30">
            <ShieldCheck className="size-6" aria-hidden />
          </div>
          <h1 className="text-xl font-semibold tracking-tight">{brandName} 運営コンソール</h1>
          <p className="text-sm text-muted-foreground">
            {stage.kind === 'credentials'
              ? '登録済みの運営者アカウントでログインしてください。'
              : '認証アプリに表示されている 6 桁のコードを入力してください。'}
          </p>
        </div>

        <div className="rounded-xl border border-border bg-card p-5 shadow-2xl shadow-black/20">
          {stage.kind === 'credentials' ? (
            <CredentialsForm
              onMfaRequired={(ticket, methods) => setStage({ kind: 'mfa', ticket, methods })}
              onDone={goHome}
            />
          ) : (
            <MfaForm
              ticket={stage.ticket}
              methods={stage.methods}
              onDone={goHome}
              onBack={() => setStage({ kind: 'credentials' })}
            />
          )}
        </div>

        <DevLoginButton serverDevLogin={config.data?.devLogin} onSuccess={goHome} />

        <p className="text-center text-xs text-muted-foreground">
          このコンソールの操作はすべて監査ログに記録されます。
        </p>
      </div>
    </div>
  );
}

function CredentialsForm({
  onMfaRequired,
  onDone,
}: {
  onMfaRequired: (ticket: string, methods: Array<'totp' | 'recovery'>) => void;
  onDone: () => void;
}): React.ReactElement {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  // 二重送信の掛け金。ここは券ではないが、**再送はレート制限の枠を焼く**
  // （ログインの上限に達すると 429 になり、正規の利用者が締め出される）。
  const gate = useSubmitOnce();

  const mut = useMutation({
    mutationFn: () => login(email.trim(), password),
    onError: gate.release,
    onSuccess: (res) => {
      if (isMfaRequired(res)) {
        onMfaRequired(res.mfaTicket, res.methods);
        return;
      }
      onDone();
    },
  });

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (gate.claim()) mut.mutate();
      }}
    >
      {mut.isError ? (
        <Alert variant="destructive">
          <AlertDescription>{errorMessageOf(mut.error)}</AlertDescription>
        </Alert>
      ) : null}

      <div className="space-y-1.5">
        <Label htmlFor="email">メールアドレス</Label>
        <div className="relative">
          <Mail
            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
            autoFocus
            className="pl-8"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="password">パスワード</Label>
        <div className="relative">
          <Lock
            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            className="pl-8"
          />
        </div>
      </div>

      <Button type="submit" className="w-full" loading={mut.isPending}>
        ログイン
      </Button>
    </form>
  );
}

/** 2 段目のフォーム。**回帰テストのために export している**（`login.test.tsx`）。 */
export function MfaForm({
  ticket,
  methods,
  onDone,
  onBack,
}: {
  ticket: string;
  methods: Array<'totp' | 'recovery'>;
  onDone: () => void;
  onBack: () => void;
}): React.ReactElement {
  const [method, setMethod] = React.useState<'totp' | 'recovery'>(methods.includes('totp') ? 'totp' : 'recovery');
  const [code, setCode] = React.useState('');

  const gate = useSubmitOnce();

  const mut = useMutation({
    mutationFn: () => verifyMfa(ticket, method, code.trim()),
    // **成功しても解放しない。** `mfaTicket` は 1 回きりで、再送は必ず
    // `MFA_TICKET_INVALID` になる（詳しい経緯は `lib/submit-once.ts`）。
    onError: gate.release,
    onSuccess: onDone,
  });

  // チケットは 5 分で失効する。切れたら黙って弾かれるより、戻す方が親切。
  React.useEffect(() => {
    const t = setTimeout(onBack, 5 * 60_000);
    return () => clearTimeout(t);
  }, [onBack]);

  /*
   * 券が死んでいるときは、**この画面で何を打っても二度と通らない**。
   * サーバーの文言（「認証セッションの有効期限が切れました。もう一度ログイン
   * してください」）だけだと、目の前の入力欄をもう一度試せばよいのか、
   * どこへ戻ればよいのかが分からない。復帰は「戻る」しかないので明示する
   * （契約者面には同じ案内が既にある）。
   */
  const ticketDead = mut.error instanceof ApiError && mut.error.code === 'MFA_TICKET_INVALID';

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (gate.claim()) mut.mutate();
      }}
    >
      {mut.isError ? (
        <Alert variant="destructive">
          <AlertDescription>
            {errorMessageOf(mut.error)}
            {ticketDead ? '「戻る」からログインし直してください。' : ''}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="space-y-1.5">
        <Label htmlFor="code">{method === 'totp' ? '認証コード（6 桁）' : 'リカバリコード'}</Label>
        <div className="relative">
          <KeyRound
            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            id="code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode={method === 'totp' ? 'numeric' : 'text'}
            autoComplete="one-time-code"
            placeholder={method === 'totp' ? '000000' : 'xxxxx-xxxxx'}
            required
            autoFocus
            className="pl-8 tracking-widest"
          />
        </div>
      </div>

      {/* 成功後も押下不能のままにする。券は消費済みで、遷移が走っている最中である。 */}
      <Button type="submit" className="w-full" loading={mut.isPending || mut.isSuccess}>
        認証する
      </Button>

      <div className="flex items-center justify-between text-xs">
        <button type="button" onClick={onBack} className="text-muted-foreground hover:text-foreground">
          戻る
        </button>
        {methods.includes('recovery') ? (
          <button
            type="button"
            onClick={() => {
              setMethod((m) => (m === 'totp' ? 'recovery' : 'totp'));
              setCode('');
            }}
            className="text-primary hover:underline"
          >
            {method === 'totp' ? 'リカバリコードを使う' : '認証アプリを使う'}
          </button>
        ) : null}
      </div>
    </form>
  );
}
